import { useEffect, useState } from "react";
import { api, type FlowVersion } from "../../api/client";
import { navigateTo, pathForTab } from "../../app/tabRoutes";
import { SEND_MSG_PATH, sendMsgCurl, type SendMsgExample } from "./sendMsgExample";

const FIELDS: { name: string; required: string; description: string }[] = [
  { name: "template-id", required: "Required", description: "The template to call with: its template ID from the Templates page." },
  { name: "to", required: "Required", description: "The number to call, in E.164 form, e.g. +15550100000." },
  { name: "from", required: "Optional", description: "Caller ID to present, in E.164 form." },
  {
    name: "uid",
    required: "Optional",
    description:
      "Your own reference for this request, up to 128 characters. Retrying with the same uid returns the first request instead of calling again.",
  },
  { name: "type", required: "Optional", description: "A label of your own, up to 64 characters, shown in Results." },
  { name: "channel", required: "Optional", description: "Must be \"voice\" if sent." },
  { name: "trunk", required: "Optional", description: "The gateway to dial through. Defaults to the server's configured trunk." },
  { name: "variables", required: "Optional", description: "A JSON object of flow variables, if the template uses any." },
];

const RESPONSES: { status: string; meaning: string }[] = [
  { status: "202", meaning: "Accepted: the call is queued (status \"pending\") and dials within a few seconds." },
  { status: "200", meaning: "Duplicate: this uid was already used for the same request. \"duplicate\": true, nothing new is dialled." },
  { status: "400", meaning: "Invalid request, or the template was not found for your key's tenant." },
  { status: "401", meaning: "Missing or invalid API key." },
  { status: "403", meaning: "The key does not have the \"place calls\" permission." },
  { status: "409", meaning: "This uid was already used for a different template or number." },
  { status: "429", meaning: "Rate limit reached for this key or tenant." },
];

/**
 * Developer → API: how to place a call from a template with
 * POST /v1/send-msg (ADR-019). Documentation only — this page makes no API
 * call except listing templates to fill in the example, and never shows a
 * real key (keys are shown once, when created on the API keys page).
 */
export function SendMsgApiPanel() {
  const [templates, setTemplates] = useState<FlowVersion[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const baseUrl = window.location.origin;

  useEffect(() => {
    let cancelled = false;
    api
      .listFlowVersions()
      .then(({ flow_versions }) => {
        if (cancelled) return;
        setTemplates(flow_versions);
        const first = flow_versions[0];
        if (first) setTemplateId((current) => current || first.flow_version_id);
      })
      .catch(() => {
        // The example still renders with a placeholder template id.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const example: SendMsgExample = {
    templateId: templateId || "YOUR_TEMPLATE_ID",
    uid: "1245346",
    to: "+15550100000",
    from: "+15550100001",
    type: "seed",
    trunk: "",
  };
  const curl = sendMsgCurl(baseUrl, example);

  async function copy(what: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied((current) => (current === what ? null : current)), 1500);
    } catch {
      // Clipboard blocked: the text is still selectable on the page.
    }
  }

  return (
    <>
      <section aria-labelledby="send-msg-heading" className="card">
        <div className="card__header">
          <div>
            <h2 id="send-msg-heading">Send a call</h2>
            <p className="card__subtitle">Place one call from a saved template with an API key.</p>
          </div>
        </div>

        <div className="api-endpoint">
          <span className="api-endpoint__method">POST</span>
          <code className="api-endpoint__url">
            {baseUrl}
            {SEND_MSG_PATH}
          </code>
          <button type="button" className="btn--outline btn--sm" onClick={() => void copy("url", `${baseUrl}${SEND_MSG_PATH}`)}>
            {copied === "url" ? "Copied" : "Copy"}
          </button>
        </div>

        <h3>Headers</h3>
        <table className="results-table api-table">
          <tbody>
            <tr>
              <td>
                <code>x-api-key</code>
              </td>
              <td>
                Your API key. Create one with <strong>Can place calls</strong> on the{" "}
                <a
                  href={pathForTab("developer")}
                  onClick={(event) => {
                    event.preventDefault();
                    navigateTo(pathForTab("developer"));
                  }}
                >
                  API keys
                </a>{" "}
                page. <code>Authorization: Bearer &lt;key&gt;</code> also works.
              </td>
            </tr>
            <tr>
              <td>
                <code>Content-Type</code>
              </td>
              <td>
                <code>application/json</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Body</h3>
        <table className="results-table api-table">
          <thead>
            <tr>
              <th>Field</th>
              <th></th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((field) => (
              <tr key={field.name}>
                <td>
                  <code>{field.name}</code>
                </td>
                <td className="api-table__required">{field.required}</td>
                <td>{field.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="send-msg-example-heading" className="card">
        <div className="card__header">
          <div>
            <h2 id="send-msg-example-heading">Example</h2>
            <p className="card__subtitle">Replace YOUR_API_KEY with a real key. Numbers shown are examples.</p>
          </div>
          <button type="button" className="btn--outline" onClick={() => void copy("curl", curl)}>
            {copied === "curl" ? "Copied" : "Copy curl"}
          </button>
        </div>
        {templates.length > 0 ? (
          <label className="field api-template-picker">
            <span>Template</span>
            <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
              {templates.map((t) => (
                <option key={t.flow_version_id} value={t.flow_version_id}>
                  {t.name} ({t.flow_version_id.slice(0, 8)})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="card__note">No templates yet: save one from the Flow designer to fill in a real template ID.</p>
        )}
        <pre className="api-code">
          <code>{curl}</code>
        </pre>

        <h3>Responses</h3>
        <table className="results-table api-table">
          <tbody>
            {RESPONSES.map((r) => (
              <tr key={r.status}>
                <td>
                  <code>{r.status}</code>
                </td>
                <td>{r.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <pre className="api-code">
          <code>{`{
    "adhoc_call_id": "2c1f0c4e-…",
    "status": "pending",
    "created_at": "2026-10-23T10:00:00Z",
    "uid": "1245346",
    "template-id": "${example.templateId}",
    "duplicate": false
}`}</code>
        </pre>
        <p className="card__note">
          Each request appears in <strong>Results</strong> with its uid and type, and can be searched by
          either.
        </p>
      </section>
    </>
  );
}
